import { DraftOrder } from '../../types/orders';
import { ExportToCsv } from 'export-to-csv';
import { formatDate, formatTime, toCommonCurrencyAmount } from '../../helpers/formatters';
import { getLocaleNumeralDecimal } from '../../helpers/intl';
import { downloadDataTablePdf } from '../../helpers/download/dataTablePdf';

export enum ActionTypes {
    GetDrafts = '[drafts] GET_DRAFTS',
    FetchDraftsResponse = '[drafts] FETCH_DRAFTS_RESPONSE',
    FetchNextResponse = '[drafts] FETCH_NEXT_RESPONSE',
    MutateDraft = '[drafts] MUTATE_DRAFT',
}

export enum GetDraftsReason {
    mount = 'mount',
    update = 'update',
    next = 'next',
    refresh = 'refresh',
}

export interface GetDrafts {
    type: ActionTypes.GetDrafts;
    payload: {
        accountId: string;
        params: URLSearchParams;
        reason: GetDraftsReason;
        requestId: number;
    };
}

export interface FetchDraftsResponse {
    type: ActionTypes.FetchDraftsResponse;
    payload: {
        drafts: DraftOrder[];
    };
    meta: {
        cause: GetDrafts;
    };
}

export interface FetchNextResponse {
    type: ActionTypes.FetchNextResponse;
    payload: {
        drafts: DraftOrder[];
    };
    meta: {
        cause: FetchDraftsResponse;
    };
}

export function getDrafts(accountId: string, params: URLSearchParams, origin: GetDraftsReason): GetDrafts {
    return {
        type: ActionTypes.GetDrafts,
        payload: {
            accountId,
            params,
            reason: origin,
            requestId: performance.now(),
        },
    };
}

export interface MutateDraft {
    type: ActionTypes.MutateDraft;
    payload: {
        draft: DraftOrder;
    };
    meta: {
        cause: FetchDraftsResponse;
    };
}

const mapOrderLine = (draft: DraftOrder, t: (o: string) => string, language: string) => ({
    // TODO expand as needed
    [t('payments.fields.date')]: formatDate(draft.created_at, language),
    [t('payments.fields.time')]: formatTime(draft.created_at, language),
    [t('payments.fields.currency')]: draft.order.currency,
    [t('payments.fields.amount')]: draft.order.net_amount
        ? toCommonCurrencyAmount(draft.order.net_amount, draft.order.currency)
        : '',
    [t('orders.fields.customer.header')]: (draft.order.customer && draft.order.customer.id) || '',
    [t('orders.fields.status')]: t(`orders.order.status.${draft.status}`),
    'Dintero ID': draft.id,
});

export function createDraftOrdersCsv(
    aid: string,
    drafts: DraftOrder[],
    t: (o: string) => string,
    language: string
): void {
    if (drafts.length === 0) {
        return;
    }
    const data = drafts.map((draft) => mapOrderLine(draft, t, language));
    const now = new Date().toISOString();
    const nowFormatted = `${formatDate(now, language)}T${formatTime(now, language)}`;
    const options = {
        fieldSeparator: ';',
        quoteStrings: '"',
        decimalSeparator: getLocaleNumeralDecimal(language),
        showLabels: true,
        showTitle: true,
        title: `Dintero drafts download ${aid}, ${nowFormatted}`,
        filename: `${aid}_${nowFormatted.replace(':', '-')}_Dintero-drafts`,
        useTextFile: false,
        useBom: true,
        useKeysAsHeaders: true,
    };
    const csvExporter = new ExportToCsv(options);
    csvExporter.generateCsv(data);
}

export function createDraftOrdersPdf(
    aid: string,
    drafts: DraftOrder[],
    t: (o: string) => string,
    language: string
): void {
    if (drafts.length === 0) {
        return;
    }
    const data = drafts.map((draft) => mapOrderLine(draft, t, language));
    const now = new Date().toISOString();
    const nowFormatted = `${formatDate(now, language)}T${formatTime(now, language)}`;
    downloadDataTablePdf(
        aid,
        `Dintero drafts download ${aid}, ${nowFormatted}`,
        nowFormatted,
        data,
        language,
        `${aid}_${nowFormatted.replace(':', '-')}_Dintero-drafts`
    );
}

export type Actions = GetDrafts | FetchDraftsResponse | FetchNextResponse | MutateDraft;
